'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import { api } from '@/lib/api';

interface Town {
  id: number;
  name: string;
  district: string;
  state: string;
}

interface ReferralAgent {
  id: number;
  name: string;
}

const COUNTRY_CODES = [
  { code: '+60', label: '🇲🇾 +60', country: 'Malaysia' },
  { code: '+65', label: '🇸🇬 +65', country: 'Singapore' },
  { code: '+62', label: '🇮🇩 +62', country: 'Indonesia' },
  { code: '+66', label: '🇹🇭 +66', country: 'Thailand' },
  { code: '+63', label: '🇵🇭 +63', country: 'Philippines' },
  { code: '+84', label: '🇻🇳 +84', country: 'Vietnam' },
  { code: '+673', label: '🇧🇳 +673', country: 'Brunei' },
  { code: '+95', label: '🇲🇲 +95', country: 'Myanmar' },
  { code: '+856', label: '🇱🇦 +856', country: 'Laos' },
  { code: '+855', label: '🇰🇭 +855', country: 'Cambodia' },
  { code: '+91', label: '🇮🇳 +91', country: 'India' },
  { code: '+86', label: '🇨🇳 +86', country: 'China' },
  { code: '+81', label: '🇯🇵 +81', country: 'Japan' },
  { code: '+82', label: '🇰🇷 +82', country: 'South Korea' },
  { code: '+61', label: '🇦🇺 +61', country: 'Australia' },
  { code: '+44', label: '🇬🇧 +44', country: 'United Kingdom' },
  { code: '+1', label: '🇺🇸 +1', country: 'United States' },
];

interface FormData {
  // Step 1: Personal Info
  fullName: string;
  displayName: string;
  phoneCountryCode: string;
  phoneNumber: string;
  whatsappCountryCode: string;
  whatsappNumber: string;
  email: string;
  townId: string;
  referralAgentId: string;
  bio: string;
  // Step 2: IC & Documents
  icNumber: string;
  icFrontImage: File | null;
  icBackImage: File | null;
  agentPhoto: File | null;
  address: string;
  experienceNotes: string;
  // Step 3: Payment & Terms
  paymentMethod: 'bank_deposit' | 'online_banking';
  paymentReceiptImage: File | null;
  paymentAmount: string;
  agreeToTerms: boolean;
  agreeToQualityStandards: boolean;
}

interface RegistrationFormProps {
  className?: string;
  onStepChange?: (step: number) => void;
}

const MEMBERSHIP_FEE = 40; // RM40 registration fee

const RegistrationForm = ({ className = '', onStepChange }: RegistrationFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [towns, setTowns] = useState<Town[]>([]);
  const [isLoadingTowns, setIsLoadingTowns] = useState(true);
  const [referralAgents, setReferralAgents] = useState<ReferralAgent[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    displayName: '',
    phoneCountryCode: '+60',
    phoneNumber: '',
    whatsappCountryCode: '+60',
    whatsappNumber: '',
    email: '',
    townId: '',
    referralAgentId: '',
    bio: '',
    icNumber: '',
    icFrontImage: null,
    icBackImage: null,
    agentPhoto: null,
    address: '',
    experienceNotes: '',
    paymentMethod: 'bank_deposit',
    paymentReceiptImage: null,
    paymentAmount: MEMBERSHIP_FEE.toString(),
    agreeToTerms: false,
    agreeToQualityStandards: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaveError, setDraftSaveError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [savedApplicationId, setSavedApplicationId] = useState<number | null>(null);

  // Town search autocomplete state
  const [townSearch, setTownSearch] = useState('');
  const [isTownDropdownOpen, setIsTownDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const townInputRef = useRef<HTMLInputElement>(null);
  const townDropdownRef = useRef<HTMLDivElement>(null);

  // Agent referral search autocomplete state
  const [agentSearch, setAgentSearch] = useState('');
  const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
  const [agentHighlightedIndex, setAgentHighlightedIndex] = useState(-1);
  const agentInputRef = useRef<HTMLInputElement>(null);
  const agentDropdownRef = useRef<HTMLDivElement>(null);

  // File input refs
  const icFrontRef = useRef<HTMLInputElement>(null);
  const icBackRef = useRef<HTMLInputElement>(null);
  const agentPhotoRef = useRef<HTMLInputElement>(null);
  const paymentReceiptRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsHydrated(true);
    fetchTowns();
    fetchReferralAgents();
  }, []);

  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  const fetchTowns = async () => {
    try {
      const data = await api.get<{ towns: Town[] }>('/towns');
      setTowns(data.towns);
    } catch (error) {
      console.error('Failed to load towns:', error);
    } finally {
      setIsLoadingTowns(false);
    }
  };

  const fetchReferralAgents = async () => {
    try {
      const data = await api.get<{ agents: ReferralAgent[] }>('/agents/referral-list');
      setReferralAgents(data.agents);
    } catch (error) {
      console.error('Failed to load referral agents:', error);
    } finally {
      setIsLoadingAgents(false);
    }
  };

  // Filtered towns for autocomplete search
  const filteredTowns = useMemo(() => {
    if (!townSearch.trim()) return towns;
    const query = townSearch.toLowerCase();
    return towns.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        t.district.toLowerCase().includes(query) ||
        t.state.toLowerCase().includes(query)
    );
  }, [towns, townSearch]);

  // Group filtered towns by state - district
  const groupedTowns = useMemo(() => {
    return filteredTowns.reduce(
      (acc, town) => {
        const key = `${town.state} - ${town.district}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(town);
        return acc;
      },
      {} as Record<string, Town[]>
    );
  }, [filteredTowns]);

  // Flat list for keyboard navigation
  const flatFilteredTowns = useMemo(() => {
    const result: Town[] = [];
    Object.values(groupedTowns).forEach((group) => result.push(...group));
    return result;
  }, [groupedTowns]);

  // Filtered agents for autocomplete search
  const filteredAgents = useMemo(() => {
    if (!agentSearch.trim()) return referralAgents;
    const query = agentSearch.toLowerCase();
    return referralAgents.filter((a) => a.name.toLowerCase().includes(query));
  }, [referralAgents, agentSearch]);

  // Sync townSearch display when towns finish loading with existing townId
  useEffect(() => {
    if (formData.townId && towns.length > 0 && !townSearch) {
      const match = towns.find((t) => t.id === parseInt(formData.townId));
      if (match) setTownSearch(match.name);
    }
  }, [towns]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        townDropdownRef.current &&
        !townDropdownRef.current.contains(e.target as Node) &&
        townInputRef.current &&
        !townInputRef.current.contains(e.target as Node)
      ) {
        setIsTownDropdownOpen(false);
      }
      if (
        agentDropdownRef.current &&
        !agentDropdownRef.current.contains(e.target as Node) &&
        agentInputRef.current &&
        !agentInputRef.current.contains(e.target as Node)
      ) {
        setIsAgentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalSteps = 3;

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Nama penuh diperlukan';
      if (!formData.displayName.trim()) newErrors.displayName = 'Nama paparan diperlukan';
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = 'No. telefon diperlukan';
      } else {
        const cleanPhone = formData.phoneNumber.replace(/[-\s]/g, '');
        if (!/^\d{7,15}$/.test(cleanPhone)) {
          newErrors.phoneNumber = 'Format telefon tidak sah (contoh: 0123456789)';
        }
      }
      if (!formData.whatsappNumber.trim()) {
        newErrors.whatsappNumber = 'No. WhatsApp diperlukan';
      } else {
        const cleanWhatsapp = formData.whatsappNumber.replace(/[-\s]/g, '');
        if (!/^\d{7,15}$/.test(cleanWhatsapp)) {
          newErrors.whatsappNumber = 'Format WhatsApp tidak sah (contoh: 0123456789)';
        }
      }
      if (!formData.email.trim()) newErrors.email = 'Email diperlukan';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Format email tidak sah';
      }
      if (!formData.townId) newErrors.townId = 'Sila pilih kawasan';
    }

    if (step === 2) {
      if (!formData.icNumber.trim()) newErrors.icNumber = 'No. IC diperlukan';
      if (!/^\d{12}$/.test(formData.icNumber.replace(/-/g, ''))) {
        newErrors.icNumber = 'Format IC tidak sah (contoh: 901234567890)';
      }
      if (!formData.icFrontImage) newErrors.icFrontImage = 'Gambar IC depan diperlukan';
      if (!formData.icBackImage) newErrors.icBackImage = 'Gambar IC belakang diperlukan';
      if (!formData.agentPhoto) newErrors.agentPhoto = 'Gambar diri diperlukan';
      if (!formData.address.trim()) newErrors.address = 'Alamat diperlukan';
    }

    if (step === 3) {
      if (formData.paymentMethod === 'bank_deposit' && !formData.paymentReceiptImage) {
        newErrors.paymentReceiptImage = 'Resit pembayaran diperlukan';
      }
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'Anda mesti bersetuju dengan terma dan syarat';
      }
      if (!formData.agreeToQualityStandards) {
        newErrors.agreeToQualityStandards = 'Anda mesti bersetuju dengan piawaian kualiti';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFileChange = (field: keyof FormData, file: File | null) => {
    setFormData((prev) => ({ ...prev, [field]: file }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleTownSelect = (town: Town) => {
    handleInputChange('townId', town.id.toString());
    setTownSearch(town.name);
    setIsTownDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleTownKeyDown = (e: React.KeyboardEvent) => {
    if (!isTownDropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsTownDropdownOpen(true);
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, flatFilteredTowns.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < flatFilteredTowns.length) {
        handleTownSelect(flatFilteredTowns[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsTownDropdownOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleAgentSelect = (agent: ReferralAgent) => {
    handleInputChange('referralAgentId', agent.id.toString());
    setAgentSearch(agent.name);
    setIsAgentDropdownOpen(false);
    setAgentHighlightedIndex(-1);
  };

  const handleAgentKeyDown = (e: React.KeyboardEvent) => {
    if (!isAgentDropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsAgentDropdownOpen(true);
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAgentHighlightedIndex((prev) => Math.min(prev + 1, filteredAgents.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAgentHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (agentHighlightedIndex >= 0 && agentHighlightedIndex < filteredAgents.length) {
        handleAgentSelect(filteredAgents[agentHighlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsAgentDropdownOpen(false);
      setAgentHighlightedIndex(-1);
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    // Step 2 → 3: save draft to backend first
    if (currentStep === 2) {
      setIsSavingDraft(true);
      setDraftSaveError('');

      try {
        const submitData = new FormData();
        submitData.append('full_name', formData.fullName);
        submitData.append('display_name', formData.displayName);
        submitData.append('phone', formData.phoneCountryCode + formData.phoneNumber.replace(/[-\s]/g, ''));
        submitData.append('whatsapp_number', formData.whatsappCountryCode + formData.whatsappNumber.replace(/[-\s]/g, ''));
        submitData.append('email', formData.email);
        submitData.append('town_id', formData.townId);
        if (formData.referralAgentId) {
          submitData.append('referred_by_agent_id', formData.referralAgentId);
        }
        submitData.append('bio', formData.bio || '');
        submitData.append('ic_number', formData.icNumber.replace(/-/g, ''));
        submitData.append('address', formData.address);
        submitData.append('experience_notes', formData.experienceNotes || '');

        if (formData.icFrontImage) submitData.append('ic_front_image', formData.icFrontImage);
        if (formData.icBackImage) submitData.append('ic_back_image', formData.icBackImage);
        if (formData.agentPhoto) submitData.append('agent_photo', formData.agentPhoto);

        const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';
        const response = await fetch(`${apiUrl}/auth/register/draft`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: submitData,
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch {
            errorData = { message: 'Fail terlalu besar. Sila kurangkan saiz gambar dan cuba lagi.' };
          }

          if (errorData.errors) {
            const fieldErrors: Partial<Record<keyof FormData, string>> = {};
            if (errorData.errors.email) { fieldErrors.email = errorData.errors.email[0]; setCurrentStep(1); }
            if (errorData.errors.phone) { fieldErrors.phoneNumber = errorData.errors.phone[0]; setCurrentStep(1); }
            if (errorData.errors.whatsapp_number) { fieldErrors.whatsappNumber = errorData.errors.whatsapp_number[0]; setCurrentStep(1); }
            if (errorData.errors.ic_number) fieldErrors.icNumber = errorData.errors.ic_number[0];
            if (errorData.errors.ic_front_image) fieldErrors.icFrontImage = errorData.errors.ic_front_image[0];
            if (errorData.errors.ic_back_image) fieldErrors.icBackImage = errorData.errors.ic_back_image[0];
            if (errorData.errors.agent_photo) fieldErrors.agentPhoto = errorData.errors.agent_photo[0];
            setErrors(fieldErrors);
          }

          setDraftSaveError(errorData.message || 'Gagal menyimpan maklumat. Sila cuba lagi.');
          return;
        }

        const result = await response.json();
        setSavedApplicationId(result.application.id);
        setCurrentStep(3);
      } catch {
        setDraftSaveError('Gagal menyimpan maklumat. Sila cuba lagi.');
      } finally {
        setIsSavingDraft(false);
      }
      return;
    }

    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const redirectToPayment = async (applicationId: number) => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';
    const intentResponse = await fetch(`${apiUrl}/payment/bayarcash/create-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ application_id: applicationId }),
    });

    if (!intentResponse.ok) {
      const intentError = await intentResponse.json();
      throw { message: intentError.message || 'Gagal memulakan pembayaran. Sila cuba lagi.' };
    }

    const intentData = await intentResponse.json();

    if (intentData.url) {
      sessionStorage.setItem('bayarcash_order_number', intentData.order_number);
      window.location.href = intentData.url;
      return;
    }

    throw { message: 'Gagal mendapatkan pautan pembayaran. Sila cuba lagi.' };
  };

  const handleRetryPayment = async () => {
    if (!savedApplicationId) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await redirectToPayment(savedApplicationId);
    } catch (err) {
      const error = err as Error;
      setSubmitError(error.message || 'Gagal memulakan pembayaran. Sila cuba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    if (!savedApplicationId) {
      setSubmitError('Maklumat permohonan belum disimpan. Sila kembali ke langkah sebelumnya.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';

      if (formData.paymentMethod === 'bank_deposit') {
        // Bank deposit: send payment data with receipt file
        const submitData = new FormData();
        submitData.append('application_id', savedApplicationId.toString());
        submitData.append('payment_amount', formData.paymentAmount);
        submitData.append('payment_method', 'bank_deposit');
        if (formData.paymentReceiptImage) {
          submitData.append('payment_receipt_image', formData.paymentReceiptImage);
        }

        const response = await fetch(`${apiUrl}/auth/register/complete`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: submitData,
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch {
            errorData = { message: 'Gagal menghantar permohonan. Sila cuba lagi.' };
          }
          throw { message: errorData.message, errors: errorData.errors };
        }

        setSubmitSuccess(true);
      } else {
        // Online banking: send payment info and get payment URL in one call
        const response = await fetch(`${apiUrl}/auth/register/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            application_id: savedApplicationId,
            payment_amount: formData.paymentAmount,
            payment_method: 'online_banking',
          }),
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch {
            errorData = { message: 'Gagal menyimpan maklumat pembayaran. Sila cuba lagi.' };
          }
          throw { message: errorData.message, errors: errorData.errors };
        }

        const data = await response.json();

        if (data.payment_url) {
          sessionStorage.setItem('bayarcash_order_number', data.order_number);
          window.location.href = data.payment_url;
          return;
        }

        // Fallback: if payment_url missing, try separate create-intent call
        try {
          await redirectToPayment(savedApplicationId);
        } catch (paymentErr) {
          const error = paymentErr as Error;
          setSubmitError(error.message || 'Gagal memulakan pembayaran. Sila cuba lagi.');
          return;
        }
      }
    } catch (err) {
      const error = err as Error & { errors?: Record<string, string[]> };
      setSubmitError(error.message || 'Gagal menghantar permohonan. Sila cuba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const FileUploadBox = ({
    label,
    file,
    onFileChange,
    inputRef,
    error,
    accept = 'image/jpeg,image/png,image/jpg',
    helpText = 'JPG, PNG (maks. 5MB)',
  }: {
    label: string;
    file: File | null;
    onFileChange: (file: File | null) => void;
    inputRef: React.RefObject<HTMLInputElement | null>;
    error?: string;
    accept?: string;
    helpText?: string;
  }) => (
    <div>
      <label className="block text-sm font-medium text-textPrimary mb-2">
        {label} <span className="text-destructive">*</span>
      </label>
      <div
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.click();
        }}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-300 ${
          file ? 'border-green-500 bg-green-50' : error ? 'border-destructive bg-red-50' : 'border-border hover:border-primary hover:bg-muted'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={(e) => onFileChange(e.target.files?.[0] || null)}
          className="hidden"
        />
        {file ? (
          <div className="flex items-center justify-center space-x-2 text-green-600">
            <Icon name="CheckCircleIcon" size={24} />
            <span className="font-medium">{file.name}</span>
          </div>
        ) : (
          <div className="text-textSecondary">
            <Icon name="CloudArrowUpIcon" size={40} className="mx-auto mb-2 text-gray-400" />
            <p className="text-sm">Klik untuk muat naik</p>
            <p className="text-xs text-gray-400 mt-1">{helpText}</p>
          </div>
        )}
      </div>
      {error && <p className="text-destructive text-sm mt-1">{error}</p>}
    </div>
  );

  if (!isHydrated) {
    return (
      <div className={`bg-card rounded-2xl shadow-lg p-8 ${className}`}>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className={`bg-card rounded-2xl shadow-lg overflow-hidden ${className}`}>
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-6">
          <div className="text-white text-center">
            <Icon name="CheckCircleIcon" size={64} className="mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold">Permohonan Berjaya Dihantar!</h3>
          </div>
        </div>
        <div className="p-8 text-center">
          <p className="text-textPrimary mb-4">
            Terima kasih kerana memohon untuk menjadi ejen Surilipat Hub.
          </p>
          <p className="text-textSecondary mb-6">
            Permohonan anda sedang disemak. Anda akan menerima email dalam masa 5 hari bekerja
            mengenai status permohonan anda.
          </p>
          <div className="bg-muted rounded-lg p-4 text-left">
            <h4 className="font-medium text-textPrimary mb-2">Proses Seterusnya:</h4>
            <ul className="list-disc list-inside space-y-1 text-textSecondary text-sm">
              <li>Semakan permohonan dan dokumen oleh admin (5 hari bekerja)</li>
              <li>Pengesahan pembayaran yuran pendaftaran</li>
              <li>Jika diluluskan, anda akan menerima email dengan pautan untuk menetapkan kata laluan</li>
              <li>Lengkapkan pendaftaran dan mula menerima tempahan</li>
            </ul>
          </div>
          <a
            href="/homepage"
            className="inline-block mt-6 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-brandDark transition-colors"
          >
            Kembali ke Laman Utama
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-2xl shadow-lg overflow-hidden ${className}`}>
      <div className="bg-gradient-to-r from-primary to-secondary p-6">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                  currentStep >= step ? 'bg-white text-primary' : 'bg-white/30 text-white'
                }`}
              >
                {step}
              </div>
              {step < 3 && (
                <div
                  className={`h-1 w-16 sm:w-24 md:w-32 mx-2 transition-all duration-300 ${
                    currentStep > step ? 'bg-white' : 'bg-white/30'
                  }`}
                ></div>
              )}
            </div>
          ))}
        </div>
        <div className="text-white">
          <h3 className="text-lg font-heading font-bold">
            {currentStep === 1 && 'Maklumat Peribadi'}
            {currentStep === 2 && 'Pengesahan IC & Dokumen'}
            {currentStep === 3 && 'Pembayaran & Terma'}
          </h3>
          <p className="text-sm opacity-90 mt-1">
            Langkah {currentStep} daripada {totalSteps}
          </p>
        </div>
      </div>

      <div className="p-6 sm:p-8">
        {submitError && savedApplicationId && formData.paymentMethod === 'online_banking' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800 font-medium mb-2">Permohonan anda telah disimpan.</p>
            <p className="text-sm text-yellow-700 mb-3">{submitError}</p>
            <button
              onClick={handleRetryPayment}
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-brandDark transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Memproses...' : 'Cuba Bayar Semula'}
            </button>
          </div>
        )}

        {submitError && !savedApplicationId && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nama Penuh <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                placeholder="Suri Siti"
              />
              {errors.fullName && <p className="text-destructive text-sm mt-1">{errors.fullName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nama Paparan (untuk pelanggan) <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                placeholder="Suri Siti"
              />
              {errors.displayName && <p className="text-destructive text-sm mt-1">{errors.displayName}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  No. Telefon <span className="text-destructive">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.phoneCountryCode}
                    onChange={(e) => handleInputChange('phoneCountryCode', e.target.value)}
                    className="w-[110px] shrink-0 px-2 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 bg-white text-sm"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className="flex-1 min-w-0 px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                    placeholder="0123456789"
                  />
                </div>
                {errors.phoneNumber && <p className="text-destructive text-sm mt-1">{errors.phoneNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  No. WhatsApp <span className="text-destructive">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.whatsappCountryCode}
                    onChange={(e) => handleInputChange('whatsappCountryCode', e.target.value)}
                    className="w-[110px] shrink-0 px-2 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 bg-white text-sm"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.whatsappNumber}
                    onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
                    className="flex-1 min-w-0 px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                    placeholder="0123456789"
                  />
                </div>
                {errors.whatsappNumber && <p className="text-destructive text-sm mt-1">{errors.whatsappNumber}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Alamat Email <span className="text-destructive">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                placeholder="siti@example.com"
              />
              {errors.email && <p className="text-destructive text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Kawasan Operasi <span className="text-destructive">*</span>
              </label>
              {isLoadingTowns ? (
                <div className="w-full px-4 py-3 border border-border rounded-lg bg-muted animate-pulse">
                  Memuatkan...
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Icon
                      name="MagnifyingGlassIcon"
                      size={18}
                      variant="outline"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <input
                      ref={townInputRef}
                      type="text"
                      value={townSearch}
                      onChange={(e) => {
                        setTownSearch(e.target.value);
                        setIsTownDropdownOpen(true);
                        setHighlightedIndex(-1);
                        if (!e.target.value.trim()) {
                          handleInputChange('townId', '');
                        }
                      }}
                      onFocus={() => setIsTownDropdownOpen(true)}
                      onKeyDown={handleTownKeyDown}
                      className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 bg-white ${
                        errors.townId ? 'border-destructive' : 'border-border'
                      }`}
                      placeholder="Cari kawasan..."
                      autoComplete="off"
                    />
                    {townSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setTownSearch('');
                          handleInputChange('townId', '');
                          townInputRef.current?.focus();
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <Icon name="XMarkIcon" size={18} variant="outline" />
                      </button>
                    )}
                  </div>
                  {isTownDropdownOpen && (
                    <div
                      ref={townDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {flatFilteredTowns.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-textSecondary">
                          Tiada kawasan dijumpai
                        </div>
                      ) : (
                        Object.entries(groupedTowns).map(([group, groupTowns]) => (
                          <div key={group}>
                            <div className="sticky top-0 px-4 py-2 bg-muted text-xs font-semibold text-textSecondary uppercase tracking-wide">
                              {group}
                            </div>
                            {groupTowns.map((town) => {
                              const idx = flatFilteredTowns.indexOf(town);
                              return (
                                <button
                                  key={town.id}
                                  type="button"
                                  onClick={() => handleTownSelect(town)}
                                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                                    idx === highlightedIndex
                                      ? 'bg-primary/10 text-primary'
                                      : formData.townId === town.id.toString()
                                        ? 'bg-accent/10 text-primary font-medium'
                                        : 'text-textPrimary hover:bg-muted'
                                  }`}
                                >
                                  {town.name}
                                </button>
                              );
                            })}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              {errors.townId && <p className="text-destructive text-sm mt-1">{errors.townId}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Ejen Perujuk (Referral)
              </label>
              {isLoadingAgents ? (
                <div className="w-full px-4 py-3 border border-border rounded-lg bg-muted animate-pulse">
                  Memuatkan...
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Icon
                      name="MagnifyingGlassIcon"
                      size={18}
                      variant="outline"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <input
                      ref={agentInputRef}
                      type="text"
                      value={agentSearch}
                      onChange={(e) => {
                        setAgentSearch(e.target.value);
                        setIsAgentDropdownOpen(true);
                        setAgentHighlightedIndex(-1);
                        if (!e.target.value.trim()) {
                          handleInputChange('referralAgentId', '');
                        }
                      }}
                      onFocus={() => setIsAgentDropdownOpen(true)}
                      onKeyDown={handleAgentKeyDown}
                      className="w-full pl-10 pr-10 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 bg-white"
                      placeholder="Cari nama ejen..."
                      autoComplete="off"
                    />
                    {agentSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setAgentSearch('');
                          handleInputChange('referralAgentId', '');
                          agentInputRef.current?.focus();
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <Icon name="XMarkIcon" size={18} variant="outline" />
                      </button>
                    )}
                  </div>
                  {isAgentDropdownOpen && (
                    <div
                      ref={agentDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {filteredAgents.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-textSecondary">
                          Tiada ejen dijumpai
                        </div>
                      ) : (
                        filteredAgents.map((agent, idx) => (
                          <button
                            key={agent.id}
                            type="button"
                            onClick={() => handleAgentSelect(agent)}
                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                              idx === agentHighlightedIndex
                                ? 'bg-primary/10 text-primary'
                                : formData.referralAgentId === agent.id.toString()
                                  ? 'bg-accent/10 text-primary font-medium'
                                  : 'text-textPrimary hover:bg-muted'
                            }`}
                          >
                            {agent.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              <p className="text-textSecondary text-xs mt-1">
                Jika anda dirujuk oleh ejen sedia ada, sila pilih nama mereka
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Bio / Penerangan Ringkas
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                placeholder="Ceritakan sedikit tentang diri anda dan pengalaman dalam perkhidmatan dobi..."
              />
            </div>
          </div>
        )}

        {/* Step 2: IC & Documents */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Icon name="InformationCircleIcon" size={20} className="text-blue-500 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Pengesahan Identiti</p>
                  <p>Dokumen ini diperlukan untuk mengesahkan identiti anda sebagai ejen Surilipat Hub.</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                No. Kad Pengenalan (IC) <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.icNumber}
                onChange={(e) => handleInputChange('icNumber', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                placeholder="901234567890"
                maxLength={14}
              />
              {errors.icNumber && <p className="text-destructive text-sm mt-1">{errors.icNumber}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileUploadBox
                label="Gambar IC (Depan)"
                file={formData.icFrontImage}
                onFileChange={(file) => handleFileChange('icFrontImage', file)}
                inputRef={icFrontRef}
                error={errors.icFrontImage}
              />

              <FileUploadBox
                label="Gambar IC (Belakang)"
                file={formData.icBackImage}
                onFileChange={(file) => handleFileChange('icBackImage', file)}
                inputRef={icBackRef}
                error={errors.icBackImage}
              />
            </div>

            <FileUploadBox
              label="Gambar Diri (Photo)"
              file={formData.agentPhoto}
              onFileChange={(file) => handleFileChange('agentPhoto', file)}
              inputRef={agentPhotoRef}
              error={errors.agentPhoto}
            />

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Alamat Penuh <span className="text-destructive">*</span>
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                placeholder="No. 123, Jalan ABC, Taman XYZ, 12345 Kuala Lumpur"
              />
              {errors.address && <p className="text-destructive text-sm mt-1">{errors.address}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Pengalaman / Catatan Tambahan
              </label>
              <textarea
                value={formData.experienceNotes}
                onChange={(e) => handleInputChange('experienceNotes', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                placeholder="Nyatakan pengalaman anda dalam perkhidmatan dobi, jika ada..."
              />
            </div>
          </div>
        )}

        {/* Step 3: Payment & Terms */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Fee display */}
            <div className="bg-accent/10 border border-accent rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="BanknotesIcon" size={24} className="text-accent mt-0.5" />
                <div>
                  <p className="font-medium text-textPrimary">Yuran Pendaftaran Ejen</p>
                  <p className="text-2xl font-bold text-primary mt-1">RM {MEMBERSHIP_FEE}.00</p>
                </div>
              </div>
            </div>

            {/* Payment method selector */}
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-3">
                Pilih Kaedah Pembayaran <span className="text-destructive">*</span>
              </label>
              <div className="space-y-3">
                {/* Bank Deposit Option */}
                <div
                  onClick={() => handleInputChange('paymentMethod', 'bank_deposit')}
                  className={`border-2 rounded-lg cursor-pointer transition-all duration-300 ${
                    formData.paymentMethod === 'bank_deposit'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center space-x-3 p-4">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.paymentMethod === 'bank_deposit'
                          ? 'border-primary'
                          : 'border-gray-300'
                      }`}
                    >
                      {formData.paymentMethod === 'bank_deposit' && (
                        <div className="w-3 h-3 rounded-full bg-primary" />
                      )}
                    </div>
                    <Icon name="BanknotesIcon" size={24} className="text-accent" />
                    <div>
                      <p className="font-medium text-textPrimary">Bank Transfer</p>
                      <p className="text-xs text-textSecondary">Anda boleh membuat bank transfer dan upload resit anda disini.</p>
                    </div>
                  </div>
                  {formData.paymentMethod === 'bank_deposit' && (
                    <div className="px-4 pb-4 space-y-4">
                      <div className="bg-white rounded-lg p-3 text-sm border border-border">
                        <p className="text-sm text-textSecondary mb-2">
                          Sila buat pembayaran ke akaun berikut dan muat naik resit pembayaran:
                        </p>
                        <p><strong>Bank:</strong> Hong Leong Bank</p>
                        <p><strong>No. Akaun:</strong> 39501237153</p>
                        <p><strong>Nama:</strong> SURI LIPAT HUB</p>
                      </div>
                      <FileUploadBox
                        label="Resit Pembayaran"
                        file={formData.paymentReceiptImage}
                        onFileChange={(file) => handleFileChange('paymentReceiptImage', file)}
                        inputRef={paymentReceiptRef}
                        error={errors.paymentReceiptImage}
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        helpText="JPG, PNG, PDF (maks. 10MB)"
                      />
                    </div>
                  )}
                </div>

                {/* Online Banking Option */}
                <div
                  onClick={() => handleInputChange('paymentMethod', 'online_banking')}
                  className={`border-2 rounded-lg cursor-pointer transition-all duration-300 ${
                    formData.paymentMethod === 'online_banking'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center space-x-3 p-4">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.paymentMethod === 'online_banking'
                          ? 'border-primary'
                          : 'border-gray-300'
                      }`}
                    >
                      {formData.paymentMethod === 'online_banking' && (
                        <div className="w-3 h-3 rounded-full bg-primary" />
                      )}
                    </div>
                    <Icon name="CreditCardIcon" size={24} className="text-primary" />
                    <div>
                      <p className="font-medium text-textPrimary">Perbankan Online (FPX)</p>
                      <p className="text-xs text-textSecondary">Bayar terus melalui perbankan online</p>
                    </div>
                  </div>
                  {formData.paymentMethod === 'online_banking' && (
                    <div className="px-4 pb-4">
                      <div className="bg-blue-50 rounded-lg p-3 text-sm border border-blue-200">
                        <p className="text-textPrimary">
                          Anda akan dialihkan ke halaman pembayaran FPX untuk membuat pembayaran{' '}
                          <strong>RM {MEMBERSHIP_FEE}.00</strong> selepas menghantar borang ini.
                        </p>
                        <p className="text-textSecondary mt-2 text-xs">
                          Pembayaran diproses dengan selamat melalui BayarCash.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-6">
              <h4 className="text-lg font-heading font-bold text-textPrimary mb-4">
                Ringkasan Permohonan
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-textSecondary">Nama Penuh:</span>
                  <span className="text-textPrimary font-medium">{formData.fullName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-textSecondary">No. IC:</span>
                  <span className="text-textPrimary font-medium">{formData.icNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-textSecondary">Email:</span>
                  <span className="text-textPrimary font-medium">{formData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-textSecondary">Telefon:</span>
                  <span className="text-textPrimary font-medium">{formData.phoneCountryCode} {formData.phoneNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-textSecondary">Kawasan:</span>
                  <span className="text-textPrimary font-medium">
                    {towns.find((t) => t.id === parseInt(formData.townId))?.name || '-'}
                  </span>
                </div>
                <div className="flex justify-between border-t border-border pt-3 mt-3">
                  <span className="text-textSecondary font-medium">Yuran Pendaftaran:</span>
                  <span className="text-primary font-bold">RM {MEMBERSHIP_FEE}.00</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-start space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:bg-muted transition-all duration-300">
                <input
                  type="checkbox"
                  checked={formData.agreeToTerms}
                  onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                  className="w-5 h-5 text-primary border-border rounded focus:ring-primary mt-0.5"
                />
                <span className="text-sm text-textPrimary">
                  Saya bersetuju dengan{' '}
                  <a href="/terma-perkhidmatan" target="_blank" className="text-primary hover:underline">
                    Terma dan Syarat
                  </a>{' '}
                  serta{' '}
                  <a href="/polisi-privasi" target="_blank" className="text-primary hover:underline">
                    Dasar Privasi
                  </a>{' '}
                  Surilipat Hub <span className="text-destructive">*</span>
                </span>
              </label>
              {errors.agreeToTerms && <p className="text-destructive text-sm">{errors.agreeToTerms}</p>}

              <label className="flex items-start space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:bg-muted transition-all duration-300">
                <input
                  type="checkbox"
                  checked={formData.agreeToQualityStandards}
                  onChange={(e) => handleInputChange('agreeToQualityStandards', e.target.checked)}
                  className="w-5 h-5 text-primary border-border rounded focus:ring-primary mt-0.5"
                />
                <span className="text-sm text-textPrimary">
                  Saya komited untuk mematuhi piawaian kualiti Surilipat Hub dan memberikan
                  perkhidmatan terbaik kepada pelanggan <span className="text-destructive">*</span>
                </span>
              </label>
              {errors.agreeToQualityStandards && (
                <p className="text-destructive text-sm">{errors.agreeToQualityStandards}</p>
              )}
            </div>
          </div>
        )}

        {draftSaveError && currentStep === 2 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{draftSaveError}</p>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          {currentStep > 1 ? (
            <button
              onClick={handlePrevious}
              disabled={isSavingDraft}
              className="flex items-center space-x-2 px-6 py-3 border border-border rounded-lg text-textPrimary hover:bg-muted transition-all duration-300 disabled:opacity-50"
            >
              <Icon name="ArrowLeftIcon" size={20} variant="outline" />
              <span className="font-medium">Kembali</span>
            </button>
          ) : (
            <div></div>
          )}

          {currentStep < totalSteps ? (
            <button
              onClick={handleNext}
              disabled={isSavingDraft}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:shadow-brand transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSavingDraft ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <span>Seterusnya</span>
                  <Icon name="ArrowRightIcon" size={20} variant="outline" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:shadow-brand transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Menghantar...</span>
                </>
              ) : formData.paymentMethod === 'online_banking' ? (
                <>
                  <Icon name="CreditCardIcon" size={20} variant="outline" />
                  <span>Bayar & Hantar</span>
                </>
              ) : (
                <>
                  <Icon name="CheckCircleIcon" size={20} variant="outline" />
                  <span>Hantar Permohonan</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
